import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import sunburstModule from 'highcharts/modules/sunburst';
import HighchartsReact from 'highcharts-react-official';
import { CHART_TYPE, DIMENSION_SEPARATOR, NA_VALUE } from '../../consts';
import css from './SunburstChart.css';

sunburstModule(Highcharts);

const PERCENTAGE_FORMAT = 'percentage';
const POSITIVE_SIGN = '+';

const formatChangeValue = (val, displayFormat) => {
    if (val == null) return NA_VALUE;
    const sign = val > 0 ? POSITIVE_SIGN : '';
    if (displayFormat?.type === PERCENTAGE_FORMAT) {
        const pct = val * 100;
        return `${sign}${pct.toFixed(displayFormat.precision ?? 2)}%`;
    }
    return sign + val.toLocaleString(undefined, { maximumFractionDigits: displayFormat?.precision ?? 2 });
};

const SunburstChart = ({ data, chartColors, metricLabels, displayFormats }) => {
    const options = useMemo(() => {
        return {
            chart: {
                type: CHART_TYPE,
                animation: false,
                spacingTop: 30,
            },
            title: { text: '' },
            credits: { enabled: false },
            exporting: { enabled: false },
            plotOptions: {
                series: {
                    animation: false,
                },
            },
            series: [
                {
                    data,
                    allowTraversingTree: true,
                    levelIsConstant: false,
                    traverseUpButton: {
                        text: '← Back',
                        relativeTo: 'spacingBox',
                        position: { align: 'left', x: -2, y: -10 },
                        theme: {
                            fill: 'none',
                            stroke: 'none',
                            'stroke-width': 0,
                            style: {
                                color: '#000000',
                                fontSize: '13px',
                            },
                            states: {
                                hover: { fill: 'none', style: { color: '#2D6CB4' } },
                            },
                        },
                    },
                    borderWidth: 2,
                    borderColor: '#FFFFFF',
                    cursor: 'pointer',
                    colorByPoint: false,
                    dataLabels: {
                        format: '{point.name}',
                        rotationMode: 'circular',
                        style: {
                            fontSize: '11px',
                            fontWeight: '500',
                            textOutline: 'none',
                            textShadow: 'none',
                            color: '#000000',
                        },
                    },
                    states: {
                        hover: {
                            brightness: 0.05,
                            halo: false,
                            borderColor: '#FFFFFF',
                            borderWidth: 0,
                            shadow: false,
                        },
                        inactive: {
                            enabled: false,
                        },
                    },
                },
            ],
            colorAxis: { min: -1, max: 1 },
            tooltip: {
                useHTML: true,
                borderColor: '#e0e0e0',
                borderWidth: 1,
                borderRadius: 8,
                shadow: false,
                hideDelay: 0,
                animation: false,
                formatter() {
                    const path = this.point.id.split(DIMENSION_SEPARATOR).join(' > ');
                    const { rawColorChange, rawSizeChange } = this.point.custom || {};
                    return (
                        `<b>${path}</b><br/>` +
                        `${metricLabels?.color}: ${formatChangeValue(rawColorChange, displayFormats?.color)}<br/>` +
                        `${metricLabels?.size}: ${formatChangeValue(rawSizeChange, displayFormats?.size)}`
                    );
                },
            },
        };
    }, [data, chartColors, metricLabels, displayFormats]);

    return (
        <div className={css.chartWrapper}>
            <HighchartsReact
                highcharts={Highcharts}
                options={options}
                containerProps={{ style: { height: '100%', width: '100%' } }}
            />
        </div>
    );
};

SunburstChart.propTypes = {
    data: PropTypes.array,
    chartColors: PropTypes.object,
    metricLabels: PropTypes.object,
    displayFormats: PropTypes.object,
};

export default SunburstChart;
